import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MagnifyingGlass, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ModalShell } from "../components/ModalShell";
import api, { fmtGNF, apiError, logError } from "../lib/api";
import { stockColorClass } from "../lib/display";
import { useAuth } from "../context/AuthContext";
import { AmountInput } from "../components/AmountInput";

const EMPTY = { name: "", category: "", purchase_price: "", sale_price: "", initial_stock: "", alert_threshold: "5", units_per_carton: "" };

export default function Stock() {
  const { can, has, estId } = useAuth();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [archived, setArchived] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const cartonAllowed = has("carton_unit");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (estId) params.set("establishment_id", estId);
    if (archived) params.set("archived", "true");
    const qs = params.toString();
    return api.get(`/products${qs ? `?${qs}` : ""}`).then((r) => setProducts(r.data)).catch((e) => logError(e));
  }, [search, estId, archived]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const create = async () => {
    try {
      await api.post("/products", {
        name: form.name, category: form.category,
        purchase_price: parseFloat(form.purchase_price) || 0, sale_price: parseFloat(form.sale_price) || 0,
        initial_stock: parseFloat(form.initial_stock) || 0, alert_threshold: parseFloat(form.alert_threshold) || 0,
        units_per_carton: cartonAllowed && form.units_per_carton ? parseFloat(form.units_per_carton) : null,
      });
      toast.success("Produit créé — aucune dépense générée");
      setShowAdd(false); setForm(EMPTY);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="stock-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-2xl">Stock</h1>
        {can("products_manage") && (
          <button data-testid="add-product-btn" onClick={() => setShowAdd(true)} className="btn-ghost py-2 px-4 flex items-center gap-1.5 text-sm">
            <Plus size={16} /> Nouveau produit
          </button>
        )}
      </div>
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3.5 top-3 text-gray-400" />
        <input data-testid="stock-search-input" className="kinput pl-11" placeholder="Rechercher un produit" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="flex gap-1.5">
        {[[false, "Actifs"], [true, "Archivés"]].map(([v, l]) => (
          <button key={l} type="button" data-testid={`stock-filter-${v ? "archived" : "active"}`} onClick={() => setArchived(v)}
            className={`text-xs px-3 py-1.5 rounded-full ${archived === v ? "bg-[#16233D] text-white" : "bg-white border border-[#E0DACE]"}`}>{l}</button>
        ))}
      </div>

      <div className="space-y-2">
        {products.map((p) => (
          <Link key={p.id} to={`/stock/${p.id}`} data-testid="product-row" className="kcard p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{p.name}</p>
              <p className="text-xs text-gray-500">{p.category || "Sans catégorie"} · Vente {fmtGNF(p.sale_price)}</p>
            </div>
            <div className="text-right">
              <p className={`font-display font-bold tabular-nums ${stockColorClass(p.stock_qty, p.alert_threshold)}`}>{p.stock_qty}</p>
              <p className="text-xs text-gray-400">en stock</p>
            </div>
          </Link>
        ))}
        {products.length === 0 && <div className="kcard p-8 text-center text-sm text-gray-400">{archived ? "Aucun produit archivé." : "Aucun produit. Créez-en un avec « Nouveau produit »."}</div>}
      </div>

      {showAdd && (
        <ModalShell size="max-w-md" onClose={() => setShowAdd(false)}>
            <h3 className="font-display font-bold text-lg">Nouveau produit</h3>
            <p data-testid="new-product-notice" className="text-xs text-gray-600 bg-[#F6F4EF] rounded-xl px-3 py-2 flex gap-2">
              <Info size={16} className="shrink-0 mt-0.5 text-[#3B6AA0]" />
              <span>Point de départ : ce que vous avez déjà en stock. Aucun fournisseur demandé, aucune dépense créée. Pour une livraison réelle, utilisez « Réceptionner du stock » sur la fiche du produit.</span>
            </p>
            <input data-testid="product-name-input" className="kinput" placeholder="Nom du produit" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="product-category-input" className="kinput" placeholder="Catégorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <AmountInput data-testid="product-purchase-input" onOverflow={(m) => toast.error(m)} placeholder="Prix d'achat (référence)" value={form.purchase_price} onChange={(v) => setForm({ ...form, purchase_price: v })} />
              <AmountInput data-testid="product-sale-input" onOverflow={(m) => toast.error(m)} placeholder="Prix de vente" value={form.sale_price} onChange={(v) => setForm({ ...form, sale_price: v })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <AmountInput data-testid="product-stock-input" onOverflow={(m) => toast.error(m)} placeholder="Stock initial" value={form.initial_stock} onChange={(v) => setForm({ ...form, initial_stock: v })} />
              <input data-testid="product-threshold-input" type="number" className="kinput" placeholder="Seuil d'alerte" value={form.alert_threshold} onChange={(e) => setForm({ ...form, alert_threshold: e.target.value })} />
            </div>
            {cartonAllowed && (
              <div>
                <input data-testid="product-carton-input" type="number" className="kinput" placeholder="Acheté par carton de X unités (optionnel)" value={form.units_per_carton} onChange={(e) => setForm({ ...form, units_per_carton: e.target.value })} />
                <p className="text-xs text-gray-500 mt-1">Vendu à l'unité — la conversion du stock est automatique lors des réceptions.</p>
              </div>
            )}
            <div className="flex gap-2">
              <button className="btn-ghost flex-1 py-2.5" onClick={() => setShowAdd(false)}>Annuler</button>
              <button data-testid="product-create-btn" className="btn-amber flex-1 py-2.5" disabled={!form.name || !form.sale_price} onClick={create}>Créer</button>
            </div>
          </ModalShell>
      )}
    </div>
  );
}
